<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'

    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { sgoto, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'

    import { Breadcrumb } from 'breadcrumb'

    import MiniKeyboard from 'wdom/components/MiniKeyboard.svelte'
    import QsDomains from './QsDomains.svelte'
    import QsOrders from './QsOrders.svelte'
    import QsUsers from './QsUsers.svelte'
    import QsZones from './QsZones.svelte'

    const DELAY_MS = 1250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
    }
    const defaults: SOptions = {
        q: '',
    }

    // When changing this, it triggers the props to search their stuff
    let effQueryText: string = $state('')

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/quick-search' : `/quick-search?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/quick-search' : `/quick-search?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        effQueryText = sOptions.q
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    function onTimeout() {
        if ($text !== sOptions.q) sOptions.q = $text
        textTimer = null
        setQueryString(sOptions)
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue === sOptions.q) {
                return
            }
            textTimer = setTimeout(onTimeout, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })
</script>

<style>
    .quick-search-result {
        /* column-count: 2;
            column-gap: 30px;
            > .panel {
                -webkit-column-break-inside: avoid;
                page-break-inside: avoid;
                break-inside: avoid;
            } */
        display: grid;
        gap: 1.5rem;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto;
        align-items: start;
    }
</style>

<Breadcrumb crumbs={[{ txt: tgettext('Quick search') }]} />

<div class="card">
    <div class="card-title text-primary">{$_('Search parameters')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
        >
            <div class="quick-search-filters">
                <div class="qsf-text">
                    <div class="input-group">
                        <input type="text" class="form-control" id="quick-search-text" placeholder={$_('Search text')} bind:this={mainInput} bind:value={$text} />
                        {#if $text}
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                        <MiniKeyboard eid={'quick-search-text'} />
                        <button class="btn btn-primary search" type="submit"><Icon inline icon="fa:search" /></button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if effQueryText}
    <div class="quick-search-result mt-4">
        <QsDomains qText={effQueryText} />
        <QsZones qText={effQueryText} />
        <QsUsers qText={effQueryText} />
        <QsOrders qText={effQueryText} />
    </div>
{/if}
